/**
 * Static Studio home / navigation / pricing content (replaces Strapi CMS).
 * Lucide icon names are bare kebab-case; UI prefixes with `lucide:` via Iconify.
 */

import type { Pricing } from '@/@account/types';
import type { Card, NavigationPage } from '@/@cms/types/entities';
import {
    STUDIO_NAV_FEATURE_FLAGS,
    type StudioLucideIcon,
    type StudioNavFeatureFlag,
} from '@/@studio/types/constants';

function card(
    partial: Omit<
        Card,
        'Clients' | 'Users' | 'isNested' | 'NestedSlug' | 'Internal' | 'TitleIcon'
    > &
        Partial<Pick<Card, 'Clients' | 'Users' | 'isNested' | 'NestedSlug' | 'Internal'>> & {
            TitleIcon: StudioLucideIcon;
            featureFlag?: StudioNavFeatureFlag | null;
        },
): Card {
    return {
        Clients: [],
        Users: [],
        isNested: null,
        NestedSlug: null,
        Internal: null,
        ...partial,
    };
}

export const STUDIO_NAVIGATION_PAGES: Record<string, NavigationPage> = {
    'current-market-trends': {
        id: 1,
        documentId: 'current-market-trends',
        slug: 'current-market-trends',
        Header: [
            {
                id: 1,
                __component: 'home.title',
                Title: 'Current Market Trends',
                Subtitle:
                    'Track, trend & visualize consumer behaviors by specific attributes & personas across app, web & venue visitation.',
                Icon: 'chart-no-axes-column',
                IconAlternate: null,
            },
        ],
        Sections: [
            {
                id: 1,
                Name: null,
                Visibility: [],
                Cards: [
                    card({
                        id: 11,
                        TitleText: 'Profile',
                        BodyText: 'View consumer demographics based on visit behavior.',
                        Link: '/current-market-trends/profiles',
                        TitleIcon: 'user',
                        Flag: 'None',
                    }),
                    card({
                        id: 12,
                        TitleText: 'Profile +',
                        BodyText: 'Compare & contrast audiences based on visit behavior.',
                        Link: '/current-market-trends/profiles_plus',
                        TitleIcon: 'user-plus',
                        Flag: 'None',
                    }),
                    card({
                        id: 13,
                        TitleText: 'Location Events Explorer',
                        BodyText: 'View, analyze & enrich visit & points of interest data.',
                        Link: '/current-market-trends/location',
                        TitleIcon: 'map-pin',
                        Flag: 'None',
                    }),
                    card({
                        id: 14,
                        TitleText: 'App Events Explorer',
                        BodyText: 'View, analyze & enrich app usage data.',
                        Link: '/current-market-trends/app',
                        TitleIcon: 'smartphone',
                        Flag: 'None',
                    }),
                    card({
                        id: 15,
                        TitleText: 'Web Events Explorer',
                        BodyText: 'View, analyze & enrich search & web traffic data.',
                        Link: '/current-market-trends/website',
                        TitleIcon: 'laptop',
                        Flag: 'None',
                    }),
                    card({
                        id: 16,
                        TitleText: 'Consumer Overlap',
                        BodyText:
                            'Evaluate overlapping behaviors of consumer across apps, web & locations.',
                        Link: '/current-market-trends/consumer_overlap',
                        TitleIcon: 'arrow-right-left',
                        Flag: 'None',
                    }),
                ],
            },
        ],
    },
    'brand-image': {
        id: 2,
        documentId: 'brand-image',
        slug: 'brand-image',
        Header: [
            {
                id: 2,
                __component: 'home.title',
                Title: 'Brand Image',
                Subtitle:
                    'In-depth assessment and perception analysis to shape and enhance brand identity',
                Icon: 'fingerprint',
                IconAlternate: null,
            },
        ],
        Sections: [
            {
                id: 2,
                Name: null,
                Visibility: [],
                Cards: [
                    card({
                        id: 17,
                        TitleText: 'Visitor Trends',
                        BodyText: 'Compare visitor trends across apps, web & locations.',
                        Link: '/brand-image/visitor_trends',
                        TitleIcon: 'trending-up',
                        Flag: 'None',
                    }),
                    card({
                        id: 18,
                        TitleText: 'Top Ranked Apps',
                        BodyText: 'Analyze top ranked apps based upon consumer use.',
                        Link: '/brand-image/top_ranked_apps',
                        TitleIcon: 'medal',
                        Flag: 'None',
                    }),
                    card({
                        id: 19,
                        TitleText: 'Top Ranked Websites',
                        BodyText:
                            'Analyze consumer web interactions with a table view of top ranked websites and traffic metrics.',
                        Link: '/brand-image/top_ranked_websites',
                        TitleIcon: 'trophy',
                        Flag: 'None',
                    }),
                    card({
                        id: 20,
                        TitleText: 'Top Ranked Venues',
                        BodyText: 'Analyze top ranked venues based upon consumer foot traffic.',
                        Link: '/brand-image/top_ranked_venues',
                        TitleIcon: 'crown',
                        Flag: 'None',
                    }),
                ],
            },
        ],
    },
    surveys: {
        id: 3,
        documentId: 'surveys',
        slug: 'surveys',
        Header: [
            {
                id: 3,
                __component: 'home.title',
                Title: 'Surveys',
                Subtitle:
                    'Build, track, and analyze survey projects — from feasibility checks through cross-tabulated results.',
                Icon: 'library',
                IconAlternate: null,
            },
        ],
        Sections: [
            {
                id: 3,
                Name: null,
                Visibility: [],
                Cards: [
                    card({
                        id: 21,
                        TitleText: 'My Surveys',
                        BodyText:
                            'Analyze & Track your survey projects. Easily blend real-time behaviors into your visual format that fits your research needs.',
                        Link: '/projects/my-surveys',
                        TitleIcon: 'clipboard',
                        Flag: 'None',
                    }),
                    card({
                        id: 22,
                        TitleText: 'Feasibility Tool',
                        BodyText:
                            'Quickly check the feasibility of your survey projects to stay aligned with your research goals and optimize success.',
                        Link: '/feasibility',
                        TitleIcon: 'map-pin-check',
                        Flag: 'None',
                        featureFlag: STUDIO_NAV_FEATURE_FLAGS.FEASIBILITY,
                    }),
                    card({
                        id: 23,
                        TitleText: 'Survey Builder',
                        BodyText:
                            'Quickly & Easily craft & launch targeted surveys to improve & accelerate decision-making.',
                        Link: '/builder',
                        TitleIcon: 'hammer',
                        Flag: 'None',
                    }),
                    card({
                        id: 24,
                        TitleText: 'Crosstabs',
                        BodyText:
                            'Streamlined for comparing variables & analyzing relationships in data.',
                        Link: '/crosstab',
                        TitleIcon: 'table-2',
                        Flag: 'None',
                    }),
                ],
            },
        ],
    },
};

export const STUDIO_PRICING: Pricing = {
    Cost: '5500',
    Features:
        '- Unlimited Users\n- Survey Pulse\n- Survey Builder\n- My Surveys\n- Crosstabs\n- Behavior Driven Dashboards',
};

export function isStudioNavigationSlug(slug: string): boolean {
    return Object.hasOwn(STUDIO_NAVIGATION_PAGES, slug);
}

export function getStudioNavigationPage(slug: string): NavigationPage | undefined {
    return isStudioNavigationSlug(slug) ? STUDIO_NAVIGATION_PAGES[slug] : undefined;
}

export function getStudioPricing(): Pricing {
    return STUDIO_PRICING;
}
